import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";

import { DetailLayout } from "@/layouts/DetailLayout/DetailLayout";
import { useAuthContext } from "@/modules/auth/hooks/useAuthContext";
import { authenticatedRouting } from "@/modules/router/routings/authenticatedRouting";
import { useGetDashboardTasksQuery } from "@/modules/tasks/components/hooks/useGetDashboardTasksQuery";
import { TaskItemsHeader } from "@/modules/tasks/components/TaskItemsHeader/TaskItemsHeader";
import { ParsedDashboardTasksItem } from "@/modules/tasks/types/types";
import { DetailLayoutBox } from "@/modules/theme/components/DetailLayoutBox";
import { PageLoader } from "@/modules/theme/components/Loader/PageLoader";

import { TaskLayoutDetails } from "../../modules/tasks/components/TaskLayoutDetails";

import { RateSubtask } from "./components/RateSubtask/RateSubtask";
import { UndoneSubtask } from "./components/UndoneSubtask/UndoneSubtask";

const translationNamespace = "pages/DashboardPage";

export const DashboardTaskDetailsPage = () => {
  const { t } = useTranslation(translationNamespace);
  const { hasAdminPrivilages, userCompany } = useAuthContext();
  const [task, setTask] = useState<ParsedDashboardTasksItem | null>(null);
  const [pickedSubtaskId, setPickedSubtaskId] = useState("");
  const params = authenticatedRouting.useRouteParams("dashboardTaskDetails");

  const {
    data: tasks,
    isLoading: isTasksDataLoading,
  } = useGetDashboardTasksQuery();

  useEffect(() => {
    if (tasks) {
      const pickedTask = tasks.find(({ id }) => id === params?.id);
      setTask(pickedTask || null);
    }
  }, [tasks, params?.id]);

  if (userCompany && !hasAdminPrivilages) {
    return <Redirect to={authenticatedRouting.buildUrl("tasks")} />;
  }

  const undoneSubtasks = task?.subtasks.filter(({ done }) => !done);

  const toRateSubtasks = task?.subtasks.filter(
    ({ done, rating }) => done && !rating
  );

  const ratedSubtasks = task?.subtasks.filter(
    ({ done, rating }) => done && rating
  );

  const handleClickSubtask = (subtaskId: string) => {
    setPickedSubtaskId((prevState) =>
      prevState === subtaskId ? "" : subtaskId
    );
  };

  return (
    <>
      {isTasksDataLoading && <PageLoader />}
      {task && (
        <DetailLayout
          variant="task"
          heading={task.name}
          details={<TaskLayoutDetails points={task.pointsNumber} />}
        >
          <DetailLayoutBox>
            {toRateSubtasks?.length ? (
              <>
                <TaskItemsHeader>{t("To rate")}</TaskItemsHeader>
                {toRateSubtasks?.map(({ id, name, pointsNumber, comment }) => (
                  <RateSubtask
                    id={id}
                    key={id}
                    name={name}
                    pointsNumber={pointsNumber}
                    comment={comment}
                    onItemClick={() => handleClickSubtask(id)}
                    isOpen={pickedSubtaskId === id}
                    rating={0}
                  />
                ))}
              </>
            ) : null}
            {undoneSubtasks?.length ? (
              <>
                <TaskItemsHeader>{t("Undone")}</TaskItemsHeader>
                {undoneSubtasks.map(({ id, name, pointsNumber }) => (
                  <UndoneSubtask
                    id={id}
                    key={id}
                    name={name}
                    pointsNumber={pointsNumber}
                  />
                ))}
              </>
            ) : null}
            {ratedSubtasks?.length ? (
              <>
                <TaskItemsHeader>{t("Rated")}</TaskItemsHeader>
                {ratedSubtasks.map(
                  ({ id, name, pointsNumber, rating, comment }) => (
                    <RateSubtask
                      id={id}
                      key={id}
                      name={name}
                      pointsNumber={pointsNumber}
                      rating={rating || 0}
                      comment={comment}
                      isOpen={pickedSubtaskId === id}
                      onItemClick={() => handleClickSubtask(id)}
                    />
                  )
                )}
              </>
            ) : null}
          </DetailLayoutBox>
        </DetailLayout>
      )}
    </>
  );
};
